import { Checkbox, Grid, Typography, styled } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import Question from "../../../../models/Question";
import { Tag } from "../../../../models/Tag";
import theme from "../../../../Theme/AppTheme";
import { useStore } from "../../../../utils/useStore";
import AcxStarRating from "../../../UI/AcxStarRating";
import { WorkflowReviewStore } from "components/WorkflowReview/Stores/WorkflowReviewStore";
import { ModuleUIModel } from "components/Evaluation/Models/ModuleUIModel";
import WFRSmartPredictToggle from "../WFRSmartPredictToggle";
import WFRAIExplanationCards from "../WFRAIExplanationCard/WFRAIExplanationCards";
import AcxQuestionDescription from "components/UI/AcxQuestionDescription";
import AcxQuestionRubric from "components/UI/AcxQuestionRubric";

// Styled component for questionHeader (used more than 3 times)
const StyledQuestionHeader = styled(Grid, {
    shouldForwardProp: (prop) => prop !== "standalone",
})<{
    standalone?: boolean;
}>(({ theme, standalone }) => ({
    marginBottom: standalone ? theme.spacing(0.25) : theme.spacing(1),
    marginTop: standalone ? theme.spacing(0.5) : theme.spacing(2),
    paddingRight: theme.spacing(2),
}));

interface WFRStarRatingAnswerProps {
    question: Question;
    moduleUIModel?: ModuleUIModel;
    standalone?: boolean;
    leftBorderColor?: string;
    isDisabled?: boolean;
    showDisputeCheckbox?: boolean;
}

function generateRatingLabels(tags: Tag[]) {
    const ratingLabels: Record<number, string> = {};
    tags!.forEach((value) => {
        ratingLabels[value.scoredValue ?? 0] = value.value;
    });
    return ratingLabels;
}

const WFRStarRatingAnswer: React.FC<WFRStarRatingAnswerProps> = observer(
    (props) => {
        const store = useStore(WorkflowReviewStore);

        const answer = store.getAnswerForQuestion(
            props.question,
            props.moduleUIModel?.licensedModule.evaluationModuleId,
        );

        const activeTag =
            answer?.activeTags[0] ??
            store.tags.find(
                (tag) => tag.id === answer?.activeAnswerTags[0]?.tagId,
            );

        const isSmartPredicted = answer?.hasAccuratePredictions;

        const starRatingTags =
            props.moduleUIModel?.getTagsFormattedForStarRating(
                props.question.id,
            );

        const maxStars = starRatingTags?.length;
        const ratingLabels = useMemo(
            () => generateRatingLabels(starRatingTags ?? []),
            [starRatingTags],
        );

        const onStarRatingChange = (rating: number | null) => {
            const arg =
                rating !== null
                    ? starRatingTags?.find(
                          (value) => value.scoredValue === rating,
                      )
                    : undefined;
            if (arg === undefined) {
                store.setAnswerForQuestion(
                    props.question,
                    [],
                    answer,
                    props.moduleUIModel?.licensedModule.evaluationModuleId,
                );
            } else {
                store.setAnswerForQuestion(
                    props.question,
                    [arg],
                    answer,
                    props.moduleUIModel?.licensedModule.evaluationModuleId,
                );
            }
        };

        let showAIExplanationCard = store.showAIExplanation[props.question.id];

        return (
            <Grid
                id={`question-${props.question.id}`}
                item
                xs={12}
                container
                direction={"column"}
                justifyContent={"flex-start"}
                alignItems="stretch"
                sx={{
                    paddingLeft:
                        props.question.parentId || props.leftBorderColor
                            ? "16px"
                            : "0px",
                    borderLeft: props.leftBorderColor
                        ? `2px solid ${props.leftBorderColor}`
                        : "none",
                }}
            >
                <StyledQuestionHeader
                    item
                    xs={12}
                    container
                    direction={"row"}
                    justifyContent={"flex-start"}
                    alignItems={"center"}
                    wrap={"nowrap"}
                    standalone={props.standalone}
                    className={clsx({
                        padding3: !props.standalone,
                    })}
                >
                    {Boolean(answer && props.showDisputeCheckbox) && (
                        <Checkbox
                            id={"DisputeAnswer-" + props.question.id}
                            title="Check to Dispute this Answer."
                            checked={answer?.isDisputed ? true : false}
                            onClick={answer?.toggleIsDisputed}
                        />
                    )}
                    <Grid item xs={10}>
                        <Grid
                            container
                            direction={"row"}
                            justifyContent={"flex-start"}
                            alignItems={"baseline"}
                            spacing={1}
                        >
                            <Grid item>
                                <Typography
                                    sx={{
                                        color: "#1F1F1F",
                                        fontSize: props.standalone
                                            ? "12px"
                                            : "14px",
                                        fontWeight: "bold",
                                        letterSpacing: "0",
                                        lineHeight: "20px",
                                        ...(answer?.isDisputed && {
                                            color: "error.main",
                                        }),
                                    }}
                                >
                                    {answer?.isDisputed && (
                                        <span
                                            style={{
                                                color: theme.palette.error.main,
                                            }}
                                            title="This answer has been escalated or disputed."
                                        >
                                            {props.question?.questionText}{" "}
                                        </span>
                                    )}
                                    {!answer?.isDisputed &&
                                        props.question?.questionText}
                                    {props.question.required &&
                                        !answer?.isAnswered && (
                                            <span
                                                style={{
                                                    color: theme.palette.error
                                                        .main,
                                                }}
                                            >
                                                *
                                            </span>
                                        )}
                                </Typography>
                            </Grid>
                            <WFRSmartPredictToggle
                                question={props.question}
                                moduleUIModel={props.moduleUIModel}
                            />
                        </Grid>
                    </Grid>
                </StyledQuestionHeader>
                <StyledQuestionHeader
                    item
                    xs={12}
                    standalone={props.standalone}
                    className={clsx({
                        padding3: !props.standalone,
                    })}
                >
                    <AcxQuestionDescription
                        text={props.question.helperText ?? ""}
                    />
                </StyledQuestionHeader>
                {!!props.question.rubric && (
                    <Grid
                        item
                        xs={12}
                        sx={{
                            paddingLeft: 3,
                            paddingRight: 3,
                            paddingBottom: 1,
                        }}
                    >
                        <AcxQuestionRubric urlString={props.question.rubric} />
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    direction={"row"}
                    wrap={"nowrap"}
                    container
                    justifyContent={"flex-start"}
                    alignItems={"flex-start"}
                    sx={{
                        ...(props.standalone
                            ? {}
                            : { paddingLeft: 3, paddingRight: 3 }),
                        height: "100%",
                        width: "100%",
                    }}
                >
                    <Grid item xs={12} style={{ flexDirection: "row" }}>
                        <AcxStarRating
                            id={"StarRatingAnswer-" + props.question.id}
                            max={maxStars ?? 3}
                            iconHoverColor={theme.palette.secondary.main}
                            iconFilledColor={theme.palette.secondary.main}
                            ratingLabels={ratingLabels}
                            onChange={onStarRatingChange}
                            value={activeTag?.scoredValue ?? 0}
                            size={"large"}
                            disabled={props.isDisabled}
                        />
                    </Grid>
                </Grid>
                {isSmartPredicted && showAIExplanationCard && activeTag && (
                    <WFRAIExplanationCards
                        activeTags={[activeTag]}
                        predictedTags={answer.predictedTags}
                        questionId={props.question.id}
                        answer={answer}
                    />
                )}
            </Grid>
        );
    },
);

export default WFRStarRatingAnswer;
